/**
 * Page-level mouse parallax (Webflow MOUSE_MOVE_IN_VIEWPORT).
 *
 * Moves a target element relative to the pointer position across the whole
 * viewport. Used for the home hero blurred circle (x: +/-50vw, y: +/-50vh).
 * Renders nothing; it only wires the listener for the duration it is mounted.
 */
import type { FC } from 'react'

import { useEffect } from 'react'

import { gsap } from '@/lib/gsap'

interface MouseParallaxProps {
    /** CSS selector for the element to move. */
    target: string
    /** Horizontal travel in vw at the screen edges. */
    xRange?: number
    /** Vertical travel in vh at the screen edges. */
    yRange?: number
    /** Smoothing duration in seconds. */
    duration?: number
}

export const MouseParallax: FC<MouseParallaxProps> = ({
    target,
    xRange = 50,
    yRange = 50,
    duration = 0.5
}) => {
    useEffect(() => {
        const element = document.querySelector<HTMLElement>(target)
        if (!element) return

        const xTo = gsap.quickTo(element, 'x', { duration, ease: 'power2.out' })
        const yTo = gsap.quickTo(element, 'y', { duration, ease: 'power2.out' })

        const onMove = (event: MouseEvent) => {
            const nx = event.clientX / window.innerWidth
            const ny = event.clientY / window.innerHeight
            const px = (xRange / 100) * window.innerWidth
            const py = (yRange / 100) * window.innerHeight

            xTo(gsap.utils.mapRange(0, 1, -px, px, nx))
            yTo(gsap.utils.mapRange(0, 1, -py, py, ny))
        }

        window.addEventListener('mousemove', onMove)

        return () => window.removeEventListener('mousemove', onMove)
    }, [target, xRange, yRange, duration])

    return null
}
